/**
 * useTextExtractionConfig Hook
 * Standardized API hooks for text extraction configuration
 *
 * Pattern: Uses createQueryHookNoParams and createMutationHook factories
 * Provides hooks for managing text extraction providers and configurations
 */

import { log } from '@/utils/errors/logger';
import { QUERY_STALE_TIME } from '@/constants/queryConfig';
import { createQueryHookNoParams, createMutationHook } from '@/hooks/api/factories';

// ============================================================================
// Query Keys
// ============================================================================

export const textExtractionQueryKeys = {
  all: ['text-extraction'] as const,
  providers: () => [...textExtractionQueryKeys.all, 'providers'] as const,
  current: () => [...textExtractionQueryKeys.all, 'current'] as const,
  allConfigs: () => [...textExtractionQueryKeys.all, 'configs'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to get available text extraction providers.
 * Returns list of supported providers (local, azure_document_intelligence, etc.).
 * Uses LONG stale time as providers rarely change.
 *
 * Usage:
 * @example
 * const { data: providers } = useGetTextExtractionProviders();
 * providers.forEach(p => console.log(p.label, p.supported_types));
 */
export const useGetTextExtractionProviders = createQueryHookNoParams<unknown[]>({
  queryKey: () => textExtractionQueryKeys.providers(),
  queryFn: (apiClient) =>
    apiClient.getAvailableProvidersApiAdminTextExtractionConfigProvidersGet().then(response => {
      log.api.response('GET', '/api/admin/text-extraction-config/providers', 200, {
        providersCount: Array.isArray(response.data) ? response.data.length : 0
      });
      return response.data;
    }),
  logContext: 'GET /api/admin/text-extraction-config/providers',
  staleTime: QUERY_STALE_TIME.LONG,
});

/**
 * Hook to get current active text extraction configuration.
 * Returns the currently active configuration with provider settings.
 * Uses MEDIUM stale time as config changes moderately.
 *
 * Usage:
 * @example
 * const { data: currentConfig } = useGetCurrentTextExtractionConfig();
 * console.log(currentConfig.provider, currentConfig.ocr_enabled);
 */
export const useGetCurrentTextExtractionConfig = createQueryHookNoParams<unknown>({
  queryKey: () => textExtractionQueryKeys.current(),
  queryFn: (apiClient) =>
    apiClient.getCurrentConfigApiAdminTextExtractionConfigCurrentGet().then(response => {
      log.api.response('GET', '/api/admin/text-extraction-config/current', 200, {
        provider: (response.data as any)?.provider
      });
      return response.data;
    }),
  logContext: 'GET /api/admin/text-extraction-config/current',
  staleTime: QUERY_STALE_TIME.MEDIUM,
});

/**
 * Hook to get all stored text extraction configurations.
 * Returns list of all saved configurations (including inactive ones).
 * Uses MEDIUM stale time as config list changes moderately.
 *
 * Usage:
 * @example
 * const { data: allConfigs } = useGetAllTextExtractionConfigs();
 * allConfigs.forEach(c => console.log(c.display_name, c.is_active));
 */
export const useGetAllTextExtractionConfigs = createQueryHookNoParams<unknown[]>({
  queryKey: () => textExtractionQueryKeys.allConfigs(),
  queryFn: (apiClient) =>
    apiClient.getAllConfigsApiAdminTextExtractionConfigAllGet().then(response => {
      log.api.response('GET', '/api/admin/text-extraction-config/all', 200, {
        configsCount: Array.isArray(response.data) ? response.data.length : 0
      });
      return response.data;
    }),
  logContext: 'GET /api/admin/text-extraction-config/all',
  staleTime: QUERY_STALE_TIME.MEDIUM,
});

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to save and activate a new text extraction configuration.
 * Invalidates current config and all configs queries on success.
 *
 * Usage:
 * @example
 * const saveConfig = useSaveTextExtractionConfig();
 * await saveConfig.mutateAsync({
 *   provider: 'azure_document_intelligence',
 *   display_name: 'Azure OCR',
 *   description: 'Production OCR config',
 *   ocr_enabled: true,
 *   extract_tables: true,
 *   provider_config: { endpoint: '...', key: '...' }
 * });
 */
export const useSaveTextExtractionConfig = createMutationHook<unknown, Record<string, unknown>>({
  mutationFn: (apiClient, config) =>
    apiClient.saveConfigApiAdminTextExtractionConfigSavePost(config).then(response => {
      log.api.response('POST', '/api/admin/text-extraction-config/save', 200, {
        provider: config.provider,
        display_name: config.display_name
      });
      return response.data;
    }),
  invalidateQueries: [
    textExtractionQueryKeys.current(),
    textExtractionQueryKeys.allConfigs(),
  ],
  logContext: 'POST /api/admin/text-extraction-config/save',
  logMethod: 'POST',
  onSuccess: (_, variables) => {
    log.user.action('text_extraction_config_saved', 'TextExtractionConfig', {
      provider: variables.provider,
      display_name: variables.display_name
    });
  },
});

/**
 * Hook to test a text extraction configuration without saving.
 * Does not invalidate any queries (test only).
 *
 * Usage:
 * @example
 * const testConfig = useTestTextExtractionConfig();
 * const result = await testConfig.mutateAsync({
 *   provider: 'local',
 *   provider_config: {}
 * });
 * console.log(result.success, result.health);
 */
export const useTestTextExtractionConfig = createMutationHook<unknown, Record<string, unknown>>({
  mutationFn: (apiClient, config) =>
    apiClient.request({
      path: '/api/admin/text-extraction-config/test',
      method: 'POST',
      body: config,
      secure: true,
    }).then(response => {
      log.api.response('POST', '/api/admin/text-extraction-config/test', 200, {
        success: (response.data as any)?.success
      });
      return response.data;
    }),
  invalidateQueries: [],
  logContext: 'POST /api/admin/text-extraction-config/test',
  logMethod: 'POST',
  onSuccess: (data) => {
    log.user.action('text_extraction_config_tested', 'TextExtractionConfig', {
      success: (data as any)?.success
    });
  },
});

/**
 * Hook to delete a specific text extraction configuration.
 * Invalidates all configs query on success.
 *
 * Usage:
 * @example
 * const deleteConfig = useDeleteTextExtractionConfig();
 * await deleteConfig.mutateAsync('config-id-123');
 */
export const useDeleteTextExtractionConfig = createMutationHook<unknown, string>({
  mutationFn: (apiClient, configId) =>
    apiClient.request({
      path: `/api/admin/text-extraction-config/${configId}`,
      method: 'DELETE',
      secure: true,
    }).then(response => {
      log.api.response('DELETE', `/api/admin/text-extraction-config/${configId}`, 200, { success: true });
      return response.data;
    }),
  invalidateQueries: [
    textExtractionQueryKeys.allConfigs(),
  ],
  logContext: 'DELETE /api/admin/text-extraction-config/:configId',
  logMethod: 'DELETE',
  onSuccess: (_data, configId) => {
    log.user.action('text_extraction_config_deleted', 'TextExtractionConfig', { configId });
  },
});

/**
 * Hook to reset text extraction config to default (environment-based).
 * Invalidates current config and all configs queries on success.
 *
 * Usage:
 * @example
 * const resetConfig = useResetTextExtractionConfig();
 * await resetConfig.mutateAsync();
 */
export const useResetTextExtractionConfig = createMutationHook<unknown, void>({
  mutationFn: (apiClient) =>
    apiClient.request({
      path: '/api/admin/text-extraction-config/reset-to-default',
      method: 'POST',
      secure: true,
    }).then(response => {
      log.api.response('POST', '/api/admin/text-extraction-config/reset-to-default', 200, { success: true });
      return response.data;
    }),
  invalidateQueries: [
    textExtractionQueryKeys.current(),
    textExtractionQueryKeys.allConfigs(),
  ],
  logContext: 'POST /api/admin/text-extraction-config/reset-to-default',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('text_extraction_config_reset', 'TextExtractionConfig');
  },
});
